import { useState } from 'react';
import {
  SERVER_NAME_MAX,
  SERVER_SIZES,
  SIZE_SPECS,
  type DiscordInstallLink,
  type GameVersion,
  type GameVersionsResponse,
  type ServerSize,
  type ServerView,
} from '@hearth/shared';
import { ApiError, type Api } from '../api';
import { navigate } from '../router';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useCached } from './cache';
import { isNew, nameProblem, releasedWhen } from './Create';
import { errorText, rateText } from './serverWords';
import { DiscordMark } from './SignIn';

// The Settings tab (M10.13, owners and admins), as the canvas board: name and size, saved together
// after a confirmation (a bigger size costs more; the size applies at the next wake); then, while
// it's asleep, a version upgrade (one way) and destroy (type its name). The sleep timer stays 30
// minutes (owner, 2026-10-22).

export interface SettingsDraft {
  name: string;
  size: ServerSize;
}

/** What Save would change, in the confirmation's words; empty when nothing would. */
export function changesText(server: ServerView, draft: SettingsDraft): string[] {
  return [
    ...(draft.name !== server.name ? [`Name: ${server.name} → ${draft.name}`] : []),
    ...(draft.size !== server.size ? [`Size: ${SIZE_SPECS[server.size].name} → ${SIZE_SPECS[draft.size].name}`] : []),
  ];
}

/** Whether the draft moves to a bigger size, which costs more each hour it's awake. */
export const costsMore = (server: ServerView, draft: SettingsDraft) => SERVER_SIZES.indexOf(draft.size) > SERVER_SIZES.indexOf(server.size);

/** The versions it may move to: those released after its own (the list is newest first). None if its own isn't listed. */
export function newerVersions(versions: readonly GameVersion[], current: string): GameVersion[] {
  const at = versions.findIndex((v) => v.version === current);
  return at < 0 ? [] : versions.slice(0, at);
}

type Asking = 'save' | 'upgrade' | 'destroy';

export function SettingsTab({ api, server, discordConnected, onChanged, onGone }: {
  api: Api;
  server: ServerView;
  /** Whether the person has a Discord account on Hearth (the panel says to connect one first). */
  discordConnected: boolean;
  onChanged: () => Promise<void>;
  onGone: () => void;
}) {
  const [draft, setDraft] = useState<SettingsDraft>({ name: server.name, size: server.size });
  const [pick, setPick] = useState<string>();
  const [asking, setAsking] = useState<Asking>();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The game's versions, kept (cache.ts) for 10 minutes: the catalog itself checks for new ones no more often.
  const versions = useCached<GameVersion[]>(`versions:${server.game}`, async () => (await api.get<GameVersionsResponse>(`/v1/games/${server.game}/versions`)).versions, {
    freshMs: 600_000,
    failed: (err) => setError(errorText(err)),
  }).data;

  // "Add to Discord" (M14.6), for owners: only they can link it there. The link is the same for every
  // server; none (no Discord app in this env, or it couldn't be read) hides the panel.
  const owner = server.role === 'owner';
  const install = useCached<string | null>(`discord-install:${owner}`, async () => (owner ? (await api.get<DiscordInstallLink>('/v1/discord/install')).url : null), {
    freshMs: 3_600_000,
    failed: () => undefined,
  }).data;

  async function act(request: () => Promise<unknown>, after: () => Promise<void> | void) {
    setBusy(true);
    try {
      await request();
      await after();
      setError('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) onGone();
      else setError(errorText(err));
    } finally {
      setBusy(false);
      setAsking(undefined);
    }
  }
  const path = `/v1/servers/${encodeURIComponent(server.serverId)}`;
  const save = () =>
    act(
      () =>
        api.patch(path, {
          ...(draft.name !== server.name ? { name: draft.name } : {}),
          ...(draft.size !== server.size ? { size: draft.size } : {}),
        }),
      onChanged,
    );
  const newer = newerVersions(versions ?? [], server.version);
  const chosen = pick ?? newer[0]?.version;
  const upgrade = () => act(() => api.post(`${path}/version`, { version: chosen }), onChanged);
  const destroy = () => act(() => api.post(`${path}/destroy`, {}), () => navigate('/app'));

  const changes = changesText(server, draft);
  return (
    <>
      {error && <p className="notice" role="alert">{error}</p>}
      <SettingsView
        server={server}
        draft={draft}
        newer={versions && newer}
        pick={chosen}
        busy={busy}
        now={new Date()}
        onDraft={setDraft}
        onSave={() => setAsking('save')}
        onPick={setPick}
        onUpgrade={() => setAsking('upgrade')}
        onDestroy={() => {
          setTyped('');
          setAsking('destroy');
        }}
        {...(install ? { discord: { installUrl: install, connected: discordConnected } } : {})}
      />
      <ConfirmDialog open={asking === 'save'} title="Save changes?" confirmLabel="Save" tone="ember" busy={busy} onConfirm={() => void save()} onCancel={() => setAsking(undefined)}>
        <ul className="changes">
          {changes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {costsMore(server, draft) && <p className="cost-note">{`A bigger size costs more each hour it's awake: ${rateText(server.size)} → ${rateText(draft.size)}.`}</p>}
        {draft.size !== server.size && <p>The new size applies at its next wake.</p>}
      </ConfirmDialog>
      <ConfirmDialog open={asking === 'upgrade'} title={`Upgrade to Minecraft ${chosen}?`} confirmLabel="Upgrade" tone="ember" busy={busy} onConfirm={() => void upgrade()} onCancel={() => setAsking(undefined)}>
        Upgrading versions cannot be undone.
      </ConfirmDialog>
      <ConfirmDialog
        open={asking === 'destroy'}
        title={`Destroy ${server.name}?`}
        confirmLabel="Destroy"
        tone="danger"
        busy={busy}
        confirmDisabled={typed !== server.name}
        onConfirm={() => void destroy()}
        onCancel={() => setAsking(undefined)}
      >
        <p>Nobody can wake or join it again. Its backups are kept.</p>
        <label className="confirm-label" htmlFor="confirm-name">
          Type <strong>{server.name}</strong> to confirm
        </label>
        <input id="confirm-name" className="field" type="text" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </ConfirmDialog>
    </>
  );
}

/** The tab, given the saved server, the edits so far and the newer versions (no fetching: easy to test). */
export function SettingsView(props: {
  server: ServerView;
  draft: SettingsDraft;
  /** Versions it may upgrade to; undefined while loading. */
  newer: GameVersion[] | undefined;
  pick: string | undefined;
  busy: boolean;
  now: Date;
  onDraft: (draft: SettingsDraft) => void;
  onSave: () => void;
  onPick: (version: string) => void;
  onUpgrade: () => void;
  onDestroy: () => void;
  /** "Add to Discord", for owners where the env has a Discord app. */
  discord?: { installUrl: string; connected: boolean };
}) {
  const { server, draft, newer, pick, busy, now, onDraft, onSave, onPick, onUpgrade, onDestroy, discord } = props;
  if (server.status === 'DESTROYED') {
    return <p className="quiet">This server was destroyed. Its backups are kept.</p>;
  }
  const problem = nameProblem(draft.name);
  const dirty = changesText(server, draft).length > 0;
  const asleep = server.status === 'STOPPED';
  return (
    <div className="settings">
      <section className="panel">
        <h2><label htmlFor="server-name">Name</label></h2>
        <input
          id="server-name"
          className="field"
          type="text"
          maxLength={SERVER_NAME_MAX}
          value={draft.name}
          aria-invalid={problem ? 'true' : undefined}
          aria-describedby={problem ? 'name-problem' : undefined}
          onChange={(e) => onDraft({ ...draft, name: e.target.value })}
        />
        {problem && <p id="name-problem" className="field-problem">{problem}</p>}
      </section>
      <section className="panel">
        <h2 id="size-setting">How many players?</h2>
        <div role="radiogroup" aria-labelledby="size-setting" className="choices sizes">
          {SERVER_SIZES.map((s) => (
            <button key={s} className={s === draft.size ? 'choice on' : 'choice'} type="button" role="radio" aria-checked={s === draft.size} onClick={() => onDraft({ ...draft, size: s })}>
              <strong>{SIZE_SPECS[s].name}</strong>
              <span className="quiet">{SIZE_SPECS[s].players}</span>
              <span className="size-rate">{rateText(s)}</span>
            </button>
          ))}
        </div>
      </section>
      {dirty && (
        <div className="save-bar">
          <button className="btn" type="button" disabled={busy} onClick={() => onDraft({ name: server.name, size: server.size })}>
            Undo changes
          </button>
          {!problem && (
            <button className="btn btn-primary" type="button" disabled={busy} onClick={onSave}>
              Save changes
            </button>
          )}
        </div>
      )}
      {discord && <DiscordPanel serverName={server.name} {...discord} />}
      <section className="panel">
        <h2 id="version-setting">Version</h2>
        <p>{`Minecraft ${server.version}`}</p>
        {!newer ? (
          <p className="quiet" aria-live="polite">Loading…</p>
        ) : newer.length === 0 ? (
          <p className="quiet">That's the newest. When a new one comes out, you can upgrade here.</p>
        ) : !asleep ? (
          <p className="quiet">A newer version is out. Put it to sleep to upgrade.</p>
        ) : (
          <>
            <div role="radiogroup" aria-labelledby="version-setting" className="versions">
              {newer.map((v) => (
                <button key={v.version} className={v.version === pick ? 'version on' : 'version'} type="button" role="radio" aria-checked={v.version === pick} onClick={() => onPick(v.version)}>
                  <span>
                    {`Minecraft ${v.version}`}
                    {isNew(v, now) && <span className="new">New</span>}
                  </span>
                  <span className="quiet">{releasedWhen(v.releasedAt, now)}</span>
                </button>
              ))}
            </div>
            <p className="quiet">Upgrades cannot be undone.</p>
            <button className="btn btn-primary" type="button" disabled={busy} onClick={onUpgrade}>
              {`Upgrade to ${pick}`}
            </button>
          </>
        )}
      </section>
      <section className="panel panel-danger">
        <h2>Destroy this server</h2>
        <p className="quiet">Gone for good. Backups retained.</p>
        {asleep ? (
          <button className="btn btn-danger-outline" type="button" disabled={busy} onClick={onDestroy}>
            Destroy
          </button>
        ) : (
          <p className="quiet">Put it to sleep first.</p>
        )}
      </section>
    </div>
  );
}

/** Waking and sleeping it from Discord: add the bot, then link it there (`/hearth link`). */
export function DiscordPanel({ serverName, installUrl, connected }: { serverName: string; installUrl: string; connected: boolean }) {
  return (
    <section className="panel">
      <h2>Discord</h2>
      <p>Wake, sleep and check this server from your Discord.</p>
      <ol className="discord-steps">
        <li>
          <span>Add Hearth to your Discord server.</span>
          <a className="btn" href={installUrl} target="_blank" rel="noopener noreferrer">
            <DiscordMark />
            Add to Discord
          </a>
        </li>
        <li>
          In the channel you want, type <code>/hearth link</code> and pick <strong>{serverName}</strong>.
        </li>
      </ol>
      {!connected && <p className="discord-note">Connect your Discord first, from your account menu (top right), so Hearth knows it's you.</p>}
      <p className="quiet">
        You'll need Manage Server in that Discord. Friends on this server can then use <code>/hearth wake</code> there too.
      </p>
    </section>
  );
}
